import React, { useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { Hotspot, question } from "../../../../../Types";
import { Interactive, useController } from "@react-three/xr";
import {
  UseAssetsQuery,
  UseDownloadAsset,
} from "../../../../../api/calls/Queries";
import * as Drei from "@react-three/drei";
import { Box, Flex, useReflow } from "@react-three/flex";
import { UseMutateAsyncFunction } from "@tanstack/react-query";
import LoadingText from "../../UI/LoadingText";
import { useSetAnswer } from "../../../../../api/calls/Quiz";
import { useCursorShrinkingStore } from "../../UI/CursorNew";
import { useXRPopupStore } from "../XRHotspots";
import { getLanguageFile } from "../../../../common/GetLanguageFile";
import { textMeshMaterial } from "./XRImagePop";
import { useMockEscapeRoomStore } from "../../../escape-room/zustand";
import QuestionHeader from "./QuizElements/QuestionHeader";
import QuestionContainer from "./QuizElements/QuestionContainer";
import QuizButtonElement from "../../UI/QuizButtonElement";
import { create } from "zustand";
import SmallTextLabel from "./QuizElements/SmallTextLabel";
import RecapPage from "./QuizElements/ErrorPage";

let timeout: NodeJS.Timeout;
let timeout2: NodeJS.Timeout;

type xrQuizStoreType = {
  answered: boolean;
  isCorrect?: boolean;

  setAnswered: (isCorrect: boolean) => void;
  setIsNotAnswered: () => void;
};

const useXrQuizStore = create<xrQuizStoreType>()((set) => ({
  answered: false,
  setAnswered: (isCorrect: boolean) =>
    set({ answered: true, isCorrect: isCorrect }),
  setIsNotAnswered: () => set({ answered: false }),
}));

const XROpenQuiz = (props: {
  question: question;
  hs: Hotspot;
  tourID: number;
  userID: number;
}) => {
  const { question, hs, tourID, userID } = props;

  const gazeController = useController("none");

  const { data: imageList } = UseAssetsQuery(props.tourID, 2);

  const imageKeyToDownload = React.useMemo(() => {
    return imageList?.find(
      (one) => one.assetId === props.question.questionMaterialeGenericoId
    )?.assetKeyFull;
  }, [imageList, props.question]);

  const { data: imageData, isLoading: isLoadingImage } =
    UseDownloadAsset(imageKeyToDownload);

  const { data, isPending, mutateAsync, isError } = useSetAnswer(
    tourID,
    question.quizId,
    userID
  );

  const [answerIds, setAnswers] = React.useState<number[]>([]);

  const { answeredQuestions } = useMockEscapeRoomStore();

  const { answered, isCorrect, setIsNotAnswered } = useXrQuizStore();

  const hasBeenAnswered = React.useMemo(() => {
    return answeredQuestions.includes(question.questionId);
  }, [question, answeredQuestions]);

  const currentQuestionType: string | undefined = React.useMemo(() => {
    switch (question?.questionTypeId) {
      case 1:
        return getLanguageFile("right_panel.single_ans");
      case 2:
        return getLanguageFile("right_panel.multi_ans");
      case 3:
        return getLanguageFile("right_panel.true_false");
      case 4:
        return getLanguageFile("right_panel.survey");
      default:
        return undefined;
    }
  }, [question?.questionTypeId]);

  const { closePop } = useXRPopupStore();

  const closePopFn = React.useMemo(() => {
    if (answered) {
      return () => {
        setIsNotAnswered();
        closePop();
      };
    } else return () => closePop();
  }, [answered, setIsNotAnswered, closePop]);

  return (
    <>
      <Flex
        onUpdate={(s) => s.lookAt(0, 0, 0)}
        centerAnchor
        justify={"center"}
        yogaDirection={"rtl"}
        position={[hs.x, hs.y, hs.z]}
        scale={gazeController ? 7 : 3.5}
        size={[10, 20, 1]}
      >
        {/* USED TEMPORARILY FOR HANDLING ERRORS */}
        {isError ? (
          <RecapPage closeBtnFn={closePopFn}>
            {getLanguageFile("quiz.answer_error")}
          </RecapPage>
        ) : (
          <>
            <LoadingText />
            <QuestionHeader question={question}></QuestionHeader>

            <QuestionContainer
              question={question}
              answerIds={answerIds}
              setAnswers={setAnswers}
              imageData={imageData}
            />

            <SmallTextLabel>{currentQuestionType}</SmallTextLabel>
            <SmallTextLabel displayed={hasBeenAnswered}>
              {getLanguageFile("viewer.already_ans")}
            </SmallTextLabel>

            <Box centerAnchor width={"100%"}>
              <AnswersWrapper
                answerIds={answerIds}
                question={question}
                setAnswers={setAnswers}
              ></AnswersWrapper>
            </Box>

            <Box centerAnchor width={"100%"}>
              <FixedBtns
                answerIds={answerIds}
                question={question}
                setAnswers={setAnswers}
                isLoading={isPending}
                mutateAsync={mutateAsync}
              />
            </Box>
          </>
        )}
      </Flex>
    </>
  );
};

const AnswersWrapper = (props: {
  question: question;
  answerIds: number[];
  setAnswers: setAnswers;
}) => {
  const { question, answerIds, setAnswers } = props;

  const reflow = useReflow();

  const wrapperHeight = React.useMemo(() => {
    const length = question.answers.length;

    return length / 2 + 1;
  }, [question.answers]);

  useFrame(() => {
    reflow();
  });
  return (
    <>
      <Flex
        centerAnchor
        width={21}
        height={wrapperHeight}
        alignContent={"space-around"}
        justifyContent={"space-between"}
        flexWrap={"wrap"}
        // size={[20, 20, 1]}
        // maxWidth={20}
        flexDir="row"
      >
        {question.answers.map((answer) => (
          <Answer
            questionType={question.questionTypeId}
            setAnswers={setAnswers}
            selected={answerIds}
            id={answer.answerId}
            key={answer.answerId}
          >
            {answer.answerText}
          </Answer>
        ))}
      </Flex>
    </>
  );
};

const Answer = (props: {
  questionType: number;
  children: string;
  selected: number[];
  id: number;
  setAnswers: setAnswers;
}) => {
  const {
    children,

    selected,
    id,
    setAnswers,
    questionType,
  } = props;

  const cursor = useCursorShrinkingStore();

  const reflow = useReflow();

  const [hover, setHover] = React.useState<number>();

  const gaze = useController("none");

  const textRef = useRef<THREE.Mesh>(null);
  const bgRef = useRef<THREE.Mesh>(null);

  const materialRef = React.useRef<THREE.MeshBasicMaterial>(null);

  const isSelected = React.useMemo(() => {
    return selected.includes(id);
  }, [selected, id]);

  useFrame(() => {
    reflow();

    if (materialRef.current) {
      if (isSelected) {
        materialRef.current.color.lerp(selectedcolor, 0.1);
        materialRef.current.opacity = 0.75;
      } else if (hover === id) {
        materialRef.current.color.lerp(hoverColor, 0.1);
      } else {
        materialRef.current.color.lerp(baseColor, 0.1);
        materialRef.current.opacity = 0.5;
      }
    }

    if (
      bgRef.current?.geometry.boundingBox?.max.y &&
      textRef.current?.geometry.boundingBox?.max.y
    ) {
      if (
        bgRef.current?.geometry.boundingBox?.max.y <
        textRef.current?.geometry.boundingBox?.max.y
      ) {
        bgRef.current.geometry.dispose();
        bgRef.current.geometry = new THREE.PlaneGeometry(
          10,
          textRef.current?.geometry.boundingBox?.max.y * 2 + 0.4
        );
      }
    } else return;
  });

  const setAnswer = React.useCallback(
    (id: number) => {
      if (questionType === 2) {
        if (isSelected) {
          let temp = selected.filter((ids) => ids !== id);

          setAnswers(temp);
        } else {
          setAnswers([...selected, id]);
        }
      } else {
        if (isSelected) {
          setAnswers([]);
        } else {
          const temp = [id];

          setAnswers(temp);
        }
      }
    },
    [questionType, selected, setAnswers, isSelected]
  );

  return (
    <Box centerAnchor width={10} marginTop={1}>
      <Interactive
        onHover={() => {
          clearTimeout(timeout);
          clearTimeout(timeout2);
          cursor.setShrinking(false);
          setHover(id);
          if (gaze) {
            timeout = setTimeout(() => {
              cursor.setShrinking(true);
              timeout2 = setTimeout(() => {
                cursor.setShrinking(false);
                setAnswer(id);
              }, 2e3);
            }, 1e3);
          }
        }}
        onBlur={() => {
          cursor.setShrinking(false);
          clearTimeout(timeout);
          clearTimeout(timeout2);
          setHover(undefined);
        }}
        onSelect={() => setAnswer(id)}
      >
        <group>
          <Drei.Text
            material={textMeshMaterial}
            ref={textRef}
            fontSize={0.9}
            outlineColor={"white"}
            outlineBlur={"5%"}
            outlineWidth={"5%"}
            outlineOpacity={isSelected ? 0.2 : 0}
            fontWeight={isSelected ? 900 : 400}
            position={[0, 0, 0.1]}
            renderOrder={3}
            maxWidth={9}
            overflowWrap="break-word"
          >
            {children}
          </Drei.Text>

          <Drei.RoundedBox
            radius={0.2}
            renderOrder={3}
            args={[10, 2]}
            ref={bgRef}
          >
            <Drei.Edges color={"#fff"} visible={isSelected} />
            <meshBasicMaterial
              ref={materialRef}
              transparent
              // opacity={0.5}
              depthTest={false}
            />
          </Drei.RoundedBox>
        </group>
      </Interactive>
    </Box>
  );
};

export default XROpenQuiz;

const FixedBtns = (props: {
  answerIds: number[];
  setAnswers: React.Dispatch<React.SetStateAction<number[]>>;
  isLoading: boolean;
  mutateAsync: UseMutateAsyncFunction<
    any,
    unknown,
    {
      questionId: number;
      answerIds: number[];
    },
    unknown
  >;
  question: question;
}) => {
  const { answerIds, setAnswers, mutateAsync, isLoading, question } = props;

  const { closePop } = useXRPopupStore();

  const { setAnswered: setAnsweredStatus } = useXrQuizStore();
  const { setAnswered, removeQuestion } = useMockEscapeRoomStore();

  const removeAnswers = React.useCallback(() => {
    closePop();
    return setAnswers([]);
  }, [closePop, setAnswers]);
  const sendAnswers = React.useCallback(() => {
    setAnswers([]);

    return mutateAsync(
      { questionId: question.questionId, answerIds: answerIds },
      {
        onSuccess: (data) => {
          if (data.success === true) {
            setAnswered(question.questionId, data.answerIsCorrect);

            removeQuestion(question.questionId);
            closePop();
          }
        },
      }
    );
  }, [
    setAnswers,
    // setAnsweredStatus,
    answerIds,
    mutateAsync,
    question.questionId,
    removeQuestion,
    setAnswered,
    closePop,
  ]);

  return (
    <Flex dir={"row"} centerAnchor justify={"center"} marginTop={2}>
      <Box centerAnchor name="answer" margin={0.5}>
        <QuizButtonElement fn={removeAnswers}>
          {getLanguageFile("generics.cancel")}
        </QuizButtonElement>
      </Box>

      <Box centerAnchor name="answer" margin={0.5}>
        <QuizButtonElement fn={sendAnswers} disabled={answerIds.length === 0}>
          {isLoading
            ? getLanguageFile("generics.loading")
            : getLanguageFile("quiz.confirm")}
        </QuizButtonElement>
      </Box>
    </Flex>
  );
};

const ConfirmationPage = () => {
  const reflow = useReflow();
  const xrPop = useXRPopupStore();
  const gaze = useController("none");

  useFrame(() => {
    reflow();
  });

  const cursor = useCursorShrinkingStore();

  return (
    <Flex align={"center"} flexDir={"column"}>
      <Box centerAnchor>
        <Drei.Text renderOrder={4}>
          {getLanguageFile("quiz.answer_error")}
        </Drei.Text>
      </Box>

      <Box centerAnchor marginTop={3}>
        <Interactive
          onHover={() => {
            clearTimeout(timeout);
            clearTimeout(timeout2);
            cursor.setShrinking(false);

            if (gaze) {
              timeout = setTimeout(() => {
                cursor.setShrinking(true);
                timeout2 = setTimeout(() => {
                  cursor.setShrinking(false);

                  xrPop.closePop();
                }, 2e3);
              }, 1e3);
            }
          }}
          onBlur={() => {
            cursor.setShrinking(false);
            clearTimeout(timeout);
            clearTimeout(timeout2);
          }}
          onSelect={() => {
            xrPop.closePop();
          }}
        >
          <Drei.Text renderOrder={4} position={[0, 0, 1]}>
            {getLanguageFile("generics.close")}
          </Drei.Text>

          <mesh
            name="answer"
            onUpdate={(self) => self.lookAt(0, 0, 0)}
            renderOrder={4}
          >
            <planeGeometry args={[6, 2]} />
            <meshBasicMaterial
              transparent
              depthTest={false}
              color={baseColor}
              opacity={0.8}
            />
          </mesh>
        </Interactive>
      </Box>
    </Flex>
  );
};

const baseColor = new THREE.Color("#0c4a6e");
const hoverColor = new THREE.Color("#1AA0ED");
const selectedcolor = new THREE.Color("#35a8e0");

type openPopup = React.Dispatch<
  React.SetStateAction<{
    isOpen: boolean;
    target: string;
    dir: THREE.Vector3 | undefined;
  }>
>;

type setAnswers = React.Dispatch<React.SetStateAction<number[]>>;
